import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, AccountAvatar, Icon, CTAButton, FAB } from '../../../components/tcn/ui-kit';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import AlertBell from '../../../components/tcn-modules/m7/AlertBell';
import ActionLogRow from '../../../components/tcn-modules/m6/ActionLogRow';
import ActionLogFilters from '../../../components/tcn-modules/m6/ActionLogFilters';
import ActionLogEditor from '../../../components/tcn-modules/m6/ActionLogEditor';
import { SOURCE_BY_ID, STATUS_BY_ID } from '../../../components/tcn-modules/m6/sources';
import { formatDubaiDate } from '../../../components/tcn-modules/shared/format';
import useIsDesktop from '../../../hooks/useIsDesktop';

/**
 * /tcn/me/actions — Module 6 — Action Log.
 *
 * RLS policy on action_log: agent_id = auth.uid() — full read/write.
 *
 * Mobile  : filter bar + list + floating "+" FAB above the tab bar.
 * Desktop : split-view (filter+list left ~40%, detail panel right ~60%).
 *
 * Cross-module entry points (URL params):
 *   ?source=tier_gap        — from Module 3 biggest_gap CTA
 *   ?source=bonus_gap       — from Module 5 biggest_bonus_gap_component CTA
 *   ?source=strategic_focus — from Module 1 strategic focus CTA (later)
 *   ?source=one_on_one_call — from Module 4 1-on-1 CTA (later)
 *   &title=... &description=...&component=...&tier=... — prefill the editor
 */
const TcnActionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPrefill, setEditorPrefill] = useState(null);

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('action_log')
      .select('*')
      .eq('agent_id', user.id)
      .order('due_date', { ascending: true })
      .order('created_at', { ascending: false });
    if (err) setError(err);
    else setActions(data || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // Pick up URL prefill (from Module 3/5 cross-module CTAs) on first mount.
  useEffect(() => {
    const src = searchParams.get('source');
    if (!src) return;
    const sourceId = SOURCE_BY_ID[src] ? src : 'creator_alert';
    const title = searchParams.get('title') || '';
    const description = searchParams.get('description') || '';
    const tier = searchParams.get('tier');
    const metric = searchParams.get('metric');
    const label = searchParams.get('label');
    const component = searchParams.get('component');
    const fallbackDesc = description
      || (label ? label : null)
      || (tier && metric ? t('tcn.m6.prefill.tierMetric', 'Tier {{tier}} · {{metric}}', { tier, metric }) : null)
      || (component ? t('tcn.m6.prefill.bonusComponent', 'Close the {{component}} bonus gap', { component }) : '');
    setEditorPrefill({
      source_activity: sourceId,
      title,
      description: fallbackDesc,
      source_entity_type: 'none',
      source_entity_id: null,
    });
    setEditorOpen(true);
    // Clear URL params so refresh doesn't re-open the editor.
    const newParams = new URLSearchParams();
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  const counts = useMemo(() => {
    const c = { all: actions.length };
    for (const a of actions) c[a.status] = (c[a.status] ?? 0) + 1;
    return c;
  }, [actions]);

  const filtered = useMemo(() => {
    return actions.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && a.source_activity !== sourceFilter) return false;
      return true;
    });
  }, [actions, statusFilter, sourceFilter]);

  const selected = useMemo(() => {
    if (!selectedId) return filtered[0] ?? null;
    return actions.find(a => a.id === selectedId) || filtered[0] || null;
  }, [actions, filtered, selectedId]);

  const openCreate = () => {
    setEditorPrefill(null);
    setEditorOpen(true);
  };

  const openEdit = (action) => {
    setEditorPrefill(action);
    setEditorOpen(true);
  };

  const handleSubmit = async (payload) => {
    if (!user?.id || !userProfile?.tenant_id || !userProfile?.agency_id) {
      throw new Error('Missing tenant/agency context');
    }
    const isEdit = !!payload.id;
    const completedAt = payload.status === 'done' && !isEdit ? new Date().toISOString() : null;
    const data = {
      ...payload,
      agent_id: user.id,
      tenant_id: userProfile.tenant_id,
      agency_id: userProfile.agency_id,
      ...(completedAt ? { completed_at: completedAt } : {}),
    };
    if (isEdit) {
      // Update — separate completed_at handling: set when transitioning to done.
      const existing = actions.find(a => a.id === payload.id);
      const transitionedToDone = existing && existing.status !== 'done' && payload.status === 'done';
      const update = { ...data };
      if (transitionedToDone && !update.completed_at) update.completed_at = new Date().toISOString();
      delete update.id;
      const { error: err } = await supabase.from('action_log').update(update).eq('id', payload.id);
      if (err) throw err;
    } else {
      const { error: err } = await supabase.from('action_log').insert(data);
      if (err) throw err;
    }
    await load();
  };

  const handleDelete = async (id) => {
    const { error: err } = await supabase.from('action_log').delete().eq('id', id);
    if (err) throw err;
    setSelectedId(null);
    await load();
  };

  const ListBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {loading && (
        <Card padded style={{ padding: 16, color: TCN.textDim, fontSize: 13 }}>
          {t('tcn.common.loading', 'Loading…')}
        </Card>
      )}
      {error && (
        <Card padded style={{ padding: 16, color: TCN.red, fontSize: 13 }}>
          {error.message}
        </Card>
      )}
      {!loading && !error && filtered.length === 0 && (
        <Card padded style={{ padding: 20, color: TCN.textDim, fontSize: 13, textAlign: 'center' }}>
          {actions.length === 0
            ? t('tcn.m6.empty.first', 'No action plans yet. Tap + to create one.')
            : t('tcn.m6.empty.filtered', 'No action plans match the filters.')}
        </Card>
      )}
      {filtered.map((a) => (
        <ActionLogRow
          key={a.id}
          action={a}
          selected={isDesktop && selected?.id === a.id}
          onSelect={(action) => {
            if (isDesktop) setSelectedId(action.id);
            else openEdit(action);
          }}
        />
      ))}
    </div>
  );

  return (
    <TcnApp
      tab="actions"
      onTabChange={handleTabChange}
      desktopUserName={userName}
      desktopBreadcrumb={[
        t('tcn.m1.breadcrumb.dashboard', 'Dashboard'),
        t('tcn.tabs.actions', 'Actions'),
      ]}
      mobileHeader={
        <TopBar
          leading={<AccountAvatar name={userName} size={36} ring />}
          title={t('tcn.m6.title', 'Action log')}
          subtitle={t('tcn.m6.subtitle', '{{count}} open · {{done}} done', {
            count: counts.open ?? 0,
            done: counts.done ?? 0,
          })}
          largeTitle
          trailing={<AlertBell />}
        />
      }
    >
      <div
        style={{
          maxWidth: isDesktop ? 1200 : '100%',
          padding: isDesktop ? 0 : '0 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ padding: isDesktop ? 0 : '0 0 4px' }}>
          <ActionLogFilters
            status={statusFilter}
            onStatusChange={setStatusFilter}
            source={sourceFilter}
            onSourceChange={setSourceFilter}
            counts={counts}
          />
        </div>

        {isDesktop ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '40fr 60fr',
              gap: 16,
              alignItems: 'start',
            }}
          >
            <div>{ListBlock}</div>
            <DetailPanel selected={selected} onEdit={() => selected && openEdit(selected)} />
          </div>
        ) : (
          ListBlock
        )}
      </div>

      <ActionLogEditor
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditorPrefill(null); }}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        initial={editorPrefill}
      />

      {/* Floating action button — mobile only (desktop has list+detail layout) */}
      {!isDesktop && (
        <FAB
          onClick={openCreate}
          icon={<Icon name="plus" size={22} />}
          ariaLabel={t('tcn.m6.fab.create', 'New action plan')}
        />
      )}
    </TcnApp>
  );
};

const DetailPanel = ({ selected, onEdit }) => {
  const { t } = useTranslation();
  if (!selected) {
    return (
      <Card padded style={{ padding: 24, color: TCN.textDim, fontSize: 13, textAlign: 'center' }}>
        {t('tcn.m6.detail.empty', 'Select an action plan to see its details.')}
      </Card>
    );
  }
  const src = SOURCE_BY_ID[selected.source_activity];
  const sta = STATUS_BY_ID[selected.status];
  return (
    <Card padded style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {src && (
          <span
            style={{
              padding: '4px 8px', borderRadius: 6,
              background: `${src.color}22`, color: src.color,
              fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}
          >
            {t(`tcn.m6.source.${src.id}`, src.label)}
          </span>
        )}
        {sta && (
          <span style={{ color: sta.color, fontSize: 12, fontWeight: 600 }}>
            {t(`tcn.m6.status.${sta.id}`, sta.label)}
          </span>
        )}
        <span style={{ marginInlineStart: 'auto', color: TCN.textDim, fontSize: 11 }}>
          {t('tcn.m6.detail.due', 'Due')} {formatDubaiDate(selected.due_date)}
        </span>
      </div>

      <h3
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: TCN.text,
          letterSpacing: '-0.02em',
          margin: '0 0 12px',
          lineHeight: 1.2,
        }}
      >
        {selected.title}
      </h3>

      <div
        style={{
          fontSize: 14,
          color: TCN.textDim,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          marginBottom: 16,
        }}
      >
        {selected.description}
      </div>

      {selected.outcome_notes && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 10,
            background: TCN.surface2,
            border: `1px solid ${TCN.border}`,
          }}
        >
          <div
            style={{
              fontSize: 10, fontWeight: 700,
              color: TCN.textMute, letterSpacing: '0.08em',
              textTransform: 'uppercase', marginBottom: 4,
            }}
          >
            {t('tcn.m6.detail.outcome', 'Outcome')}
          </div>
          <div style={{ fontSize: 13, color: TCN.text, whiteSpace: 'pre-wrap' }}>
            {selected.outcome_notes}
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
        <CTAButton variant="primary" onClick={onEdit} fullWidth={false}>
          {t('tcn.m6.detail.edit', 'Edit')}
        </CTAButton>
      </div>
    </Card>
  );
};

export default TcnActionsPage;
